/**
 * API functions for CRM integration
 */
import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

interface CRMApiResponse {
  api_response: {
    message: string;
    status: string;
  };
  message: string;
  org_id: string;
  success: boolean;
}

export type OwnerAccountStatus = 'ok' | 'non_owner_key' | 'not_applicable';

/**
 * Shown when a connected FUB key isn't an owner-level key — surfaced both as a
 * connect-time warning toast and as a persistent banner.
 */
export const NON_OWNER_KEY_WARNING =
  "This API key isn't from the FUB owner account. Webhook sync requires an owner-level API key. Please reconnect using an owner key.";

/**
 * Integration health for an organization's connected CRM, served by remi-api.
 * Only the fields the frontend consumes are typed; the endpoint returns
 * additional diagnostic signals callers can read through the index signature.
 */
export interface IntegrationHealth {
  success: boolean;
  organization_id: string;
  connected: boolean;
  integration_type: string | null;
  credential_status: string;
  token_status: string;
  webhook_status: string;
  stage_config_status: string;
  owner_account_status: OwnerAccountStatus;
  recommended_action: string;
  [key: string]: unknown;
}

/**
 * Get integration health for an organization
 * @param organizationId The organization ID
 * @returns A promise that resolves to the integration health report
 */
export const getIntegrationHealth = async (
  organizationId: string
): Promise<IntegrationHealth> => {
  const response = await remiApi.get<IntegrationHealth>(
    `/api/v1/organizations/${organizationId}/integration-health`
  );
  return response.data;
};

/**
 * Add a client to CRM
 * @param organizationId The organization ID
 * @param email The client email
 * @param password The client password
 * @returns A promise that resolves to true if successful
 */
export const addCRMClient = async (
  organizationId: string,
  email: string,
  password: string
): Promise<boolean> => {
  if (!organizationId || !email || !password) {
    throw new Error('Organization ID, email, and password are required');
  }

  try {
    const response = await remiApi.post<CRMApiResponse>(
      '/api/v1/integrations/add-external-client',
      {
        org_id: organizationId,
        email: email,
        password: password,
      }
    );

    const data = response.data;

    if (!data.success) {
      throw new Error('Failed to add CRM integration');
    }

    console.log('CRM client added successfully:', data.message);
    return true;
  } catch (error) {
    console.error('Error adding CRM client:', error);
    throw error;
  }
};
